/**
 * RBAC v2 фаза (d) — чистая логика конструктора ролей (AK-343, ADR-040).
 *
 * Зеркалит backend-контракт `/api/v1/roles` (roles.py) и каталог ресурсов
 * (role_presets.py `RESOURCE_CATALOG`, 11 ресурсов). Здесь только чистые
 * функции — матрица-стейт, диф «было → стало», валидация имени/кода. Всё
 * тестируется vitest'ом без React (см. rolesLogic.test.ts).
 */

/** Действие CRUD. Порядок фиксирован — совпадает с колонками матрицы. */
export const ACTIONS = ['C', 'R', 'U', 'D'] as const;
export type Action = (typeof ACTIONS)[number];

/** RU-подписи действий (для дифа и подсказок). */
export const ACTION_LABELS_RU: Record<Action, string> = {
  C: 'создание',
  R: 'просмотр',
  U: 'изменение',
  D: 'удаление',
};

/**
 * Каталог 11 ресурсов — 1:1 с backend `RESOURCE_CATALOG` (role_presets.py).
 * `code` обязан совпадать с бэком (валидатор матрицы отвергает чужие ресурсы).
 */
export interface ResourceDef {
  code: string;
  name: string;
  hint: string;
}

export const RESOURCE_CATALOG: readonly ResourceDef[] = [
  { code: 'fields', name: 'Поля и культуры', hint: 'контуры, кадастр, посевы' },
  { code: 'structure', name: 'Оргструктура', hint: 'компании/кластеры/хозяйства/бригады' },
  { code: 'scoutings', name: 'Осмотры', hint: 'осмотры и инспекции полей' },
  { code: 'scouting_reports', name: 'Отчёты об осмотре', hint: 'сгенерированные отчёты по осмотрам' },
  { code: 'operations', name: 'Операции', hint: 'сев/обработка/уборка, техкарты' },
  { code: 'machinery', name: 'Техника', hint: 'техника, ТО, ГСМ' },
  { code: 'reports', name: 'Аналитика', hint: 'дашборды, экспорт, BI' },
  { code: 'team', name: 'Команда', hint: 'участники и приглашения' },
  { code: 'satellite', name: 'Спутники', hint: 'NDVI, снимки' },
  { code: 'company_reference', name: 'Справочники', hint: 'культуры, типы операций' },
  { code: 'company_settings', name: 'Настройки и роли', hint: 'реквизиты, бренд, RBAC-матрица' },
] as const;

export const RESOURCE_CODES: readonly string[] = RESOURCE_CATALOG.map((r) => r.code);
const RESOURCE_SET = new Set(RESOURCE_CODES);

/** Пресеты — 1:1 с backend PRESETS (role_presets.py). */
export const PRESET_CODES = ['agroholding', 'kolkhoz'] as const;
export type PresetCode = (typeof PRESET_CODES)[number];

export const PRESET_LABELS: Record<PresetCode, { name: string; hint: string }> = {
  agroholding: { name: 'Агрохолдинг', hint: 'R-центрично' },
  kolkhoz: { name: 'Колхоз', hint: 'CRUD-центрично' },
};

// ─── API-контракт (1:1 с roles.py Pydantic-схемами) ──────────────────────────

export interface PermissionRow {
  resource: string;
  can_create: boolean;
  can_read: boolean;
  can_update: boolean;
  can_delete: boolean;
  scope_bound: boolean;
}

export interface RoleSummary {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  is_system: boolean;
  preset_code?: string | null;
  scope_kinds: string[];
  member_count: number;
}

export interface RoleDetail extends RoleSummary {
  permissions: PermissionRow[];
  system_note?: string | null;
}

export interface RolesList {
  system_roles: RoleSummary[];
  custom_roles: RoleSummary[];
}

export interface MemberRoleDiff {
  role_id: string;
  code: string;
  name: string;
  is_system: boolean;
  member_count: number;
}

// ─── Матрица-стейт ───────────────────────────────────────────────────────────

/**
 * Матрица в UI = map ресурс → набор флагов. Каждый ресурс из каталога
 * присутствует всегда (даже с нулевыми правами), чтобы UI рисовал полную
 * таблицу 11 строк.
 */
export type MatrixState = Record<string, PermissionRow>;

/** Пустая строка матрицы для ресурса (по умолчанию scope_bound=true как бэк). */
export function emptyRow(resource: string): PermissionRow {
  return {
    resource,
    can_create: false,
    can_read: false,
    can_update: false,
    can_delete: false,
    scope_bound: true,
  };
}

/** Пустая матрица: 11 ресурсов, все права сняты. */
export function emptyMatrix(): MatrixState {
  const m: MatrixState = {};
  for (const r of RESOURCE_CATALOG) m[r.code] = emptyRow(r.code);
  return m;
}

/**
 * Собрать матрицу из ответа API: недостающие ресурсы заполняются пустыми
 * строками (напр. системная роль без строки по ресурсу → нули, не пропуск).
 */
export function matrixFromPermissions(perms: PermissionRow[]): MatrixState {
  const m = emptyMatrix();
  for (const p of perms) {
    if (RESOURCE_SET.has(p.resource)) {
      m[p.resource] = { ...emptyRow(p.resource), ...p, resource: p.resource };
    }
  }
  return m;
}

/**
 * Матрица → массив строк для отправки на бэк. Отправляем ВСЕ 11 ресурсов
 * (бэк делает replace-all; строки без прав тоже валидны и явны).
 */
export function matrixToPermissions(m: MatrixState): PermissionRow[] {
  return RESOURCE_CODES.map((code) => m[code] ?? emptyRow(code));
}

export type CellField = 'can_create' | 'can_read' | 'can_update' | 'can_delete';

const ACTION_FIELD: Record<Action, CellField> = {
  C: 'can_create',
  R: 'can_read',
  U: 'can_update',
  D: 'can_delete',
};

export function fieldForAction(a: Action): CellField {
  return ACTION_FIELD[a];
}

/** Переключить одну ячейку (иммутабельно). */
export function toggleCell(m: MatrixState, resource: string, action: Action): MatrixState {
  const row = m[resource] ?? emptyRow(resource);
  const field = ACTION_FIELD[action];
  return { ...m, [resource]: { ...row, [field]: !row[field] } };
}

/** Переключить scope_bound строки. */
export function toggleScopeBound(m: MatrixState, resource: string): MatrixState {
  const row = m[resource] ?? emptyRow(resource);
  return { ...m, [resource]: { ...row, scope_bound: !row.scope_bound } };
}

/** Быстрый пресет строки: полный доступ / только просмотр / снять всё. */
export type RowPreset = 'full' | 'read' | 'none';

export function applyRowPreset(m: MatrixState, resource: string, preset: RowPreset): MatrixState {
  const row = m[resource] ?? emptyRow(resource);
  const flags =
    preset === 'full'
      ? { can_create: true, can_read: true, can_update: true, can_delete: true }
      : preset === 'read'
        ? { can_create: false, can_read: true, can_update: false, can_delete: false }
        : { can_create: false, can_read: false, can_update: false, can_delete: false };
  return { ...m, [resource]: { ...row, ...flags } };
}

/** Тот же пресет на все 11 строк (кнопки шапки матрицы). */
export function applyAllPreset(m: MatrixState, preset: RowPreset): MatrixState {
  let next = m;
  for (const code of RESOURCE_CODES) next = applyRowPreset(next, code, preset);
  return next;
}

// ─── Диф «было → стало» (пункт 6) ────────────────────────────────────────────

export interface DiffCell {
  resource: string;
  resourceName: string;
  action: Action;
  /** true = право добавлено (+), false = снято (−). */
  added: boolean;
}

/** Подсвеченные ячейки в матрице: resource → [C,U,U,D] изменилась ли. */
export type ChangedCells = Record<string, Record<Action, boolean>>;

export interface MatrixDiff {
  cells: DiffCell[];
  changed: ChangedCells;
  hasChanges: boolean;
}

const resourceName = (code: string): string =>
  RESOURCE_CATALOG.find((r) => r.code === code)?.name ?? code;

/**
 * Диф между исходной и текущей матрицей — только CRUD-ячейки (scope_bound в
 * диф не входит, показывается отдельно как «в своей зоне»). Человекочитаемо:
 * группируем добавленные/снятые права по ресурсу.
 */
export function computeMatrixDiff(before: MatrixState, after: MatrixState): MatrixDiff {
  const cells: DiffCell[] = [];
  const changed: ChangedCells = {};
  for (const code of RESOURCE_CODES) {
    const b = before[code] ?? emptyRow(code);
    const a = after[code] ?? emptyRow(code);
    const rowChanged: Record<Action, boolean> = { C: false, R: false, U: false, D: false };
    for (const action of ACTIONS) {
      const field = ACTION_FIELD[action];
      if (b[field] !== a[field]) {
        rowChanged[action] = true;
        cells.push({
          resource: code,
          resourceName: resourceName(code),
          action,
          added: a[field],
        });
      }
    }
    if (rowChanged.C || rowChanged.R || rowChanged.U || rowChanged.D) {
      changed[code] = rowChanged;
    }
  }
  return { cells, changed, hasChanges: cells.length > 0 };
}

/**
 * Сгруппированный человекочитаемый диф по ресурсу: «Техника — создание,
 * изменение (C, U)». Отдельно добавленные (+) и снятые (−) права.
 */
export interface DiffLine {
  resource: string;
  resourceName: string;
  added: boolean;
  actions: Action[];
  /** «создание, изменение (C, U)» */
  text: string;
}

export function summarizeDiff(diff: MatrixDiff): DiffLine[] {
  const byKey = new Map<string, DiffLine>();
  for (const c of diff.cells) {
    const key = `${c.resource}:${c.added ? '+' : '-'}`;
    const existing = byKey.get(key);
    if (existing) {
      existing.actions.push(c.action);
    } else {
      byKey.set(key, {
        resource: c.resource,
        resourceName: c.resourceName,
        added: c.added,
        actions: [c.action],
      } as DiffLine);
    }
  }
  const lines = Array.from(byKey.values());
  for (const line of lines) {
    // Порядок действий — как в ACTIONS (C,R,U,D).
    line.actions.sort((x, y) => ACTIONS.indexOf(x) - ACTIONS.indexOf(y));
    const words = line.actions.map((a) => ACTION_LABELS_RU[a]).join(', ');
    const codes = line.actions.join(', ');
    line.text = `${words} (${codes})`;
  }
  // Сначала добавленные (+), потом снятые (−); внутри — по названию ресурса.
  lines.sort((a, b) => {
    if (a.added !== b.added) return a.added ? -1 : 1;
    return a.resourceName.localeCompare(b.resourceName, 'ru');
  });
  return lines;
}

// ─── Валидация имени / кода ───────────────────────────────────────────────────

/**
 * Системные имена ролей (для inline-проверки code-collision, ревью #402).
 * Список RU-меток системных ролей; бэк финально валидирует по `code`, но
 * UI даёт мгновенную подсказку по совпадению имени.
 */
export const SYSTEM_ROLE_NAMES_RU: readonly string[] = [
  'Администратор компании',
  'Администратор',
  'Агроном',
  'Наблюдатель',
  'Юрист',
  'Бригадир',
  'Скаут',
  'Техник',
  'Менеджер',
  'Бухгалтер',
  'Консультант',
  'Учётчик',
];

const normalize = (s: string): string => s.trim().toLowerCase().replace(/\s+/g, ' ');

export interface NameValidation {
  ok: boolean;
  error?: string;
}

/**
 * Проверка имени кастомной роли:
 *   - не пустое;
 *   - не совпадает с именем системной роли (code-collision guard, #402);
 *   - не дублирует другую кастомную роль тенанта.
 */
export function validateRoleName(
  name: string,
  existingCustomNames: readonly string[],
  currentId?: string | null,
  existingCustom?: readonly RoleSummary[],
): NameValidation {
  const trimmed = name.trim();
  if (!trimmed) return { ok: false, error: 'Введите название роли' };

  const norm = normalize(trimmed);
  if (SYSTEM_ROLE_NAMES_RU.some((n) => normalize(n) === norm)) {
    return { ok: false, error: 'Имя занято системной ролью — выберите другое' };
  }

  // Дубль среди кастомных — исключая саму редактируемую роль.
  if (existingCustom && currentId) {
    const clash = existingCustom.some(
      (r) => r.id !== currentId && normalize(r.name) === norm,
    );
    if (clash) return { ok: false, error: 'Роль с таким именем уже есть в вашем хозяйстве' };
  } else if (existingCustomNames.some((n) => normalize(n) === norm)) {
    return { ok: false, error: 'Роль с таким именем уже есть в вашем хозяйстве' };
  }

  return { ok: true };
}

/**
 * Транслитерация имени → code (латиница + подчёркивания), для генерации
 * `code` кастомной роли при создании. Бэк всё равно валидирует уникальность
 * и code-collision, но осмысленный code лучше рандома для аудита.
 */
const TRANSLIT: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z',
  и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r',
  с: 's', т: 't', у: 'u', ф: 'f', х: 'h', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'sch',
  ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
};

export function slugifyRoleCode(name: string): string {
  const base = name
    .trim()
    .toLowerCase()
    .split('')
    .map((ch) => TRANSLIT[ch] ?? ch)
    .join('')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_{2,}/g, '_');
  const trimmed = base.slice(0, 48) || 'role';
  // code min_length=2 на бэке.
  const code = trimmed.length >= 2 ? trimmed : `${trimmed}_role`;
  // Уникализируем коротким суффиксом — избегаем гонки по (tenant,code).
  return `${code}_${Math.random().toString(36).slice(2, 6)}`;
}

/** Подпись «в своей зоне / по всей компании» для scope_bound. */
export function scopeBoundLabel(scopeBound: boolean): string {
  return scopeBound ? 'в своей зоне' : 'по всей компании';
}

/** Сводная строка счётчика прав роли для карточки списка. */
export function permCountLabel(perms: PermissionRow[]): number {
  return perms.reduce(
    (sum, p) =>
      sum +
      (p.can_create ? 1 : 0) +
      (p.can_read ? 1 : 0) +
      (p.can_update ? 1 : 0) +
      (p.can_delete ? 1 : 0),
    0,
  );
}
